/**
 * Blood Test Accordions - Open All on Single Click (Desktop Only)
 * 
 * When any blood test accordion is clicked on desktop, opens all blood test accordions.
 * Works only on desktop (min-width: 750px).
 */

class BloodTestAccordions {
  constructor() {
    this.isDesktop = () => window.innerWidth >= 750;
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }

    // Handle window resize to update desktop state
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        // Re-setup if needed
      }, 250);
    });
  }

  /**
   * Get all blood test accordions
   */
  getBloodTestAccordions() {
    const allAccordions = document.querySelectorAll('accordion-custom');
    return Array.from(allAccordions).filter(accordion => {
      const summary = accordion.querySelector('summary');
      if (!summary) return false;
      
      const headingText = summary.textContent || '';
      // Check if heading contains "Blood Test" (case-insensitive)
      return /blood\s+test/i.test(headingText);
    });
  }

  /**
   * Open all blood test accordions
   */
  openAllBloodTestAccordions() {
    const bloodTestAccordions = this.getBloodTestAccordions();
    
    bloodTestAccordions.forEach(accordion => {
      const details = accordion.querySelector('details');
      if (details && !details.open) {
        details.open = true;
      }
    });
  }

  /**
   * Close all blood test accordions
   */
  closeAllBloodTestAccordions() {
    const bloodTestAccordions = this.getBloodTestAccordions();
    
    bloodTestAccordions.forEach(accordion => {
      const details = accordion.querySelector('details');
      if (details && details.open) {
        details.open = false;
      }
    });
  }

  setup() {
    // Only setup on desktop
    if (!this.isDesktop()) return;

    const bloodTestAccordions = this.getBloodTestAccordions();
    
    if (bloodTestAccordions.length === 0) return;

    // Add click handlers to each blood test accordion summary
    bloodTestAccordions.forEach(accordion => {
      const summary = accordion.querySelector('summary');
      if (!summary) return;

      // Use a flag to prevent infinite loops
      let isOpeningAll = false;

      summary.addEventListener('click', (event) => {
        // Only work on desktop
        if (!this.isDesktop()) return;

        // If we're already processing, let the default behavior happen
        if (isOpeningAll) {
          isOpeningAll = false;
          return;
        }

        // Check current state of this accordion
        const details = accordion.querySelector('details');
        const willOpen = !details.open;

        // If clicking to open, open all blood test accordions
        // If clicking to close, close all blood test accordions
        if (willOpen) {
          isOpeningAll = true;
          
          // Use setTimeout to let the clicked accordion open first, then open others
          setTimeout(() => {
            this.openAllBloodTestAccordions();
            isOpeningAll = false;
          }, 10);
        } else {
          // Closing - close all blood test accordions
          isOpeningAll = true;
          
          // Use setTimeout to let the clicked accordion close first, then close others
          setTimeout(() => {
            this.closeAllBloodTestAccordions();
            isOpeningAll = false;
          }, 10);
        }
      });
    });

    // Also listen for the toggle event on details elements
    bloodTestAccordions.forEach(accordion => {
      const details = accordion.querySelector('details');
      if (!details) return;

      details.addEventListener('toggle', (event) => {
        // Only work on desktop
        if (!this.isDesktop()) return;

        // If opening, open all others; if closing, close all others
        if (details.open) {
          setTimeout(() => {
            this.openAllBloodTestAccordions();
          }, 10);
        } else {
          setTimeout(() => {
            this.closeAllBloodTestAccordions();
          }, 10);
        }
      });
    });
  }

  /**
   * Public method to manually open all blood test accordions
   */
  openAll() {
    if (!this.isDesktop()) return;
    this.openAllBloodTestAccordions();
  }

  /**
   * Public method to manually close all blood test accordions
   */
  closeAll() {
    if (!this.isDesktop()) return;
    this.closeAllBloodTestAccordions();
  }
}

// Initialize Blood Test Accordions functionality
const bloodTestAccordions = new BloodTestAccordions();

// Export for use in other scripts if needed
if (typeof module !== 'undefined' && module.exports) {
  module.exports = BloodTestAccordions;
}
